import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { ChevronDown, ChevronUp } from 'lucide-react';
import DownloadTAInputs from '../../components/DownloadTAInputs/DownloadTAInputs';
import PageTitle from '../../components/PageTitle/PageTitle';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const InputCard = ({ title, value }) => (
  <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-100 hover:shadow-md transition-shadow flex justify-between items-center">
    <p className="text-sm text-gray-600">{title}</p>
    <p className="text-sm font-medium text-gray-900 ml-2">{value ?? 'N/A'}</p>
  </div>
);

const InputSection = ({ title, inputs }) => (
  <div className="space-y-2">
    <h4 className="font-semibold text-gray-900 text-sm mb-2">{title}</h4>
    {/* Adjust grid layout to reduce horizontal space */}
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      {inputs.map(({ title, value }) => (
        <InputCard key={title} title={title} value={value} />
      ))}
    </div>
  </div>
);

const FitToTAAnalysis = () => {
  const location = useLocation();
  const { pacingData = {} } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [fundData, setFundData] = useState({});
  const [expandedFunds, setExpandedFunds] = useState({});

  useEffect(() => {
    if (pacingData && Object.keys(pacingData).length > 0) {
      setFundData(pacingData);
      const initialExpandState = {};
      Object.keys(pacingData).forEach(fundName => {
        initialExpandState[fundName] = true;
      });
      setExpandedFunds(initialExpandState);
    }
  }, [pacingData]);

  const buildChartData = (cashflows) => {
    const sortedPeriods = Object.keys(cashflows)
      .map((periodStr) => parseFloat(periodStr))
      .filter((val) => !isNaN(val))
      .sort((a, b) => a - b);

    const contributions = sortedPeriods.map(
      (p) => cashflows[p].inputted_contributions || 0
    );
    const distributions = sortedPeriods.map(
      (p) => cashflows[p].inputted_distributions || 0
    );
    const modelContributions = sortedPeriods.map(
      (p) => cashflows[p].model_contributions || 0
    );
    const modelDistributions = sortedPeriods.map(
      (p) => cashflows[p].model_distributions || 0
    );

    return {
      labels: sortedPeriods.map((p) => (p / 4).toFixed(2)),
      datasets: [
        {
          type: 'bar',
          label: 'Contributions',
          data: contributions,
          backgroundColor: 'rgba(59, 130, 246, 0.7)'
        },
        {
          type: 'bar',
          label: 'Distributions',
          data: distributions,
          backgroundColor: 'rgba(239, 68, 68, 0.7)'
        },
        {
          type: 'line',
          label: 'Model Contributions',
          data: modelContributions,
          borderColor: 'rgba(59, 130, 246, 1)',
          borderWidth: 2,
          fill: false
        },
        {
          type: 'line',
          label: 'Model Distributions',
          data: modelDistributions,
          borderColor: 'rgba(239, 68, 68, 1)',
          borderWidth: 2,
          fill: false
        }
      ]
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  if (Object.keys(fundData).length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center p-8 bg-white rounded-xl shadow-sm max-w-lg">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Data Available</h3>
          <p className="text-gray-600">
            Please ensure you have the correct data loaded before proceeding with the analysis.
          </p>
        </div>
      </div>
    );
  }

  const toggleFund = (fundName) => {
    setExpandedFunds(prev => ({
      ...prev,
      [fundName]: !prev[fundName]
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <PageTitle 
            title="Fit Cashflows to Pacing Model"
            subtitle="Analysis Dashboard"
          />
        </div>

        <div className="flex justify-center mb-8">
          <DownloadTAInputs fundData={fundData} />
        </div>

        {Object.keys(fundData).map((fundName) => {
          const { ta_inputs = {}, cashflows = {} } = fundData[fundName];
          const chartData = buildChartData(cashflows);
          const isExpanded = expandedFunds[fundName];

          const sections = [
            {
              title: "Growth Rates",
              inputs: [
                { title: "Years 1-2", value: `${ta_inputs.growthYears1to2}%` },
                { title: "Years 3-5", value: `${ta_inputs.growthYears3to5}%` },
                { title: "Years 6+", value: `${ta_inputs.growthYears6Plus}%` }
              ]
            },
            {
              title: "Contribution Rates",
              inputs: [
                { title: "Year 1", value: `${ta_inputs.contributionRateYear1}%` },
                { title: "Year 2", value: `${ta_inputs.contributionRateYear2}%` },
                { title: "Year 3+", value: `${ta_inputs.contributionRateYear3Plus}%` }
              ]
            },
            {
              title: "Other Parameters",
              inputs: [
                { title: "Yield", value: `${ta_inputs.yield}%` },
                { title: "Bow", value: ta_inputs.bow },
                { title: "Fund Life", value: `${ta_inputs.fundLifeYears} Years` }
              ]
            }
          ];

          return (
            <div key={fundName} className="mb-8 bg-white rounded-xl shadow-lg overflow-hidden">
              <button
                onClick={() => toggleFund(fundName)}
                className="w-full px-6 py-4 flex justify-between items-center bg-white border-b border-gray-200"
              >
                <h2 className="text-xl font-bold text-gray-900">{fundName}</h2>
                {isExpanded ? (
                  <ChevronUp className="h-5 w-5 text-gray-600" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-600" />
                )}
              </button>

              {isExpanded && (
                <div className="p-6">
                  <div className="flex flex-col lg:flex-row gap-6">
                    {/* Left side - Model Inputs */}
                    <div className="lg:w-1/3">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Model Inputs</h3>
                      <div className="grid grid-cols-1 gap-4">
                        {sections.map((section) => (
                          <InputSection
                            key={section.title}
                            title={section.title}
                            inputs={section.inputs}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Right side - Chart */}
                    <div className="lg:w-2/3">
                    <div className="h-96">
                        <Bar
                          data={chartData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                position: 'top',
                                labels: {
                                  padding: 20,
                                  usePointStyle: true
                                }
                              },
                              title: {
                                display: true,
                                text: 'Cashflow Projection',
                                padding: 20,
                                font: {
                                  size: 16,
                                  weight: 'bold'
                                }
                              }
                            },
                            scales: {
                              x: {
                                title: {
                                  display: true,
                                  text: 'Years',
                                  padding: 10
                                },
                                grid: {
                                  display: false
                                }
                              },
                              y: {
                                stacked: false,
                                grid: {
                                  color: 'rgba(0, 0, 0, 0.05)'
                                }
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FitToTAAnalysis;